/**
 * Warnings View
 * Displays allocation warnings with dismiss functionality
 */
import { useAllocationWarnings, useDismissWarning, useRefreshWarnings } from '@/hooks/useAllocation';
import { useAllocationStore, selectFilters } from '@/store/allocationStore';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import {
  ASSET_CLASS_DISPLAY_NAMES,
  STRATEGIC_ROLE_DISPLAY_NAMES,
  type AllocationWarning,
  type AllocationWarningType,
  type WarningSeverity,
  type WarningStatus,
} from '@/types/allocation';

const WARNING_TYPE_LABELS: Record<AllocationWarningType, string> = {
  portfolio_drift: 'Portfolio Drift',
  bucket_drift: 'Bucket Drift',
  account_below_target: 'Below Target',
  account_above_target: 'Above Target',
  missing_classification: 'Missing Classification',
  unassigned_account: 'Unassigned Account',
};

const SEVERITY_COLORS: Record<WarningSeverity, { bg: string; text: string; icon: string }> = {
  info: { bg: 'bg-blue-50', text: 'text-blue-800', icon: 'text-blue-500' },
  warning: { bg: 'bg-yellow-50', text: 'text-yellow-800', icon: 'text-yellow-500' },
  alert: { bg: 'bg-red-50', text: 'text-red-800', icon: 'text-red-500' },
};

export default function WarningsView() {
  const filters = useAllocationStore(selectFilters);
  const setWarningStatusFilter = useAllocationStore((state) => state.setWarningStatusFilter);

  const statusParam: WarningStatus | undefined =
    filters.warningStatusFilter === 'all' ? undefined : filters.warningStatusFilter as WarningStatus;

  const { data, isLoading } = useAllocationWarnings({ status: statusParam });
  const { mutate: refreshWarnings, isPending: isRefreshing } = useRefreshWarnings();

  if (isLoading || !data) {
    return <WarningsSkeleton />;
  }

  const { warnings, summary } = data;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <Card>
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Allocation Warnings
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Alerts about allocation drift and issues
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refreshWarnings()}
              isLoading={isRefreshing}
            >
              Refresh Warnings
            </Button>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-4">
            <SummaryItem label="Total" value={summary.total} color="text-gray-900" />
            <SummaryItem
              label="Alerts"
              value={summary.bySeverity.alert}
              color="text-red-600"
            />
            <SummaryItem
              label="Warnings"
              value={summary.bySeverity.warning}
              color="text-yellow-600"
            />
            <SummaryItem
              label="Info"
              value={summary.bySeverity.info}
              color="text-blue-600"
            />
          </div>
        </div>
      </Card>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <label className="text-sm text-gray-600">Status:</label>
        <select
          value={filters.warningStatusFilter}
          onChange={(e) =>
            setWarningStatusFilter(e.target.value as 'active' | 'dismissed' | 'all')
          }
          className="rounded-md border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="active">Active</option>
          <option value="dismissed">Dismissed</option>
          <option value="all">All</option>
        </select>
      </div>

      {/* Warnings list */}
      {warnings.length === 0 ? (
        <EmptyState statusFilter={filters.warningStatusFilter} />
      ) : (
        <div className="space-y-4">
          {warnings.map((warning) => (
            <WarningCard key={warning.id} warning={warning} />
          ))}
        </div>
      )}
    </div>
  );
}

interface SummaryItemProps {
  label: string;
  value: number;
  color: string;
}

function SummaryItem({ label, value, color }: SummaryItemProps) {
  return (
    <div>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${color}`}>{value}</p>
    </div>
  );
}

interface WarningCardProps {
  warning: AllocationWarning;
}

function WarningCard({ warning }: WarningCardProps) {
  const { mutate: dismissWarning, isPending } = useDismissWarning();
  const colors = SEVERITY_COLORS[warning.severity];

  const handleDismiss = () => {
    dismissWarning({ warningId: warning.id });
  };

  const handleDismissFor = (days: number) => {
    const dismissUntil = new Date();
    dismissUntil.setDate(dismissUntil.getDate() + days);
    dismissWarning({
      warningId: warning.id,
      data: { dismissUntil: dismissUntil.toISOString() },
    });
  };

  return (
    <Card className={`${colors.bg} border-l-4 ${colors.text.replace('text-', 'border-')}`}>
      <div className="p-4">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <SeverityIcon severity={warning.severity} className={`h-5 w-5 ${colors.icon}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className={`text-sm font-medium ${colors.text}`}>
                  {warning.title}
                </p>
                <p className={`mt-1 text-sm ${colors.text} opacity-80`}>
                  {warning.message}
                </p>
              </div>
              <span className={`flex-shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
                {WARNING_TYPE_LABELS[warning.warningType]}
              </span>
            </div>

            {/* Details */}
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
              {warning.assetClass && (
                <span className="text-gray-600">
                  Asset: {ASSET_CLASS_DISPLAY_NAMES[warning.assetClass]}
                </span>
              )}
              {warning.strategicRole && (
                <span className="text-gray-600">
                  Bucket: {STRATEGIC_ROLE_DISPLAY_NAMES[warning.strategicRole]}
                </span>
              )}
              {warning.driftPercentage && (
                <span className="text-gray-600">
                  Drift: {parseFloat(warning.driftPercentage).toFixed(1)}%
                </span>
              )}
              <span className="text-gray-500">
                {formatDate(warning.createdAt)}
              </span>
            </div>

            {/* Actions */}
            {warning.status === 'active' && (
              <div className="mt-3 flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDismiss}
                  isLoading={isPending}
                >
                  Dismiss
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDismissFor(7)}
                  disabled={isPending}
                >
                  Dismiss for 7 days
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDismissFor(30)}
                  disabled={isPending}
                >
                  Dismiss for 30 days
                </Button>
              </div>
            )}

            {warning.status === 'dismissed' && warning.dismissUntil && (
              <p className="mt-2 text-sm text-gray-500">
                Dismissed until {formatDate(warning.dismissUntil)}
              </p>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

interface SeverityIconProps {
  severity: WarningSeverity;
  className?: string;
}

function SeverityIcon({ severity, className }: SeverityIconProps) {
  if (severity === 'info') {
    return (
      <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
      </svg>
    );
  }

  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  );
}

interface EmptyStateProps {
  statusFilter: string;
}

function EmptyState({ statusFilter }: EmptyStateProps) {
  return (
    <Card>
      <div className="p-12 text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
          <CheckIcon className="h-6 w-6 text-green-600" />
        </div>
        <h3 className="mt-4 text-lg font-medium text-gray-900">
          {statusFilter === 'active'
            ? 'No active warnings'
            : statusFilter === 'dismissed'
            ? 'No dismissed warnings'
            : 'No warnings'}
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          {statusFilter === 'active'
            ? 'Your allocation is on track. Great job!'
            : "You haven't dismissed any warnings yet."}
        </p>
      </div>
    </Card>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

function WarningsSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="grid grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      </Card>
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <div className="p-4 animate-pulse">
              <div className="h-5 bg-gray-200 rounded w-1/2 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-3/4" />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
